import { usePostHog } from "@posthog/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ExternalLink, Loader2, X } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardContent,
	DashboardHeaderSection,
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { BlueskyConnectDialog } from "@/components/platforms/BlueskyConnectDialog";
import {
	PLATFORM_ICONS,
	type PlatformKey,
} from "@/components/platforms/platform-icons";
import { useSession } from "@/hooks/auth-hooks";
import {
	parseOnboardingContentFormats,
	parseOnboardingPriorityPlatforms,
	sortPlatformsByOnboardingPreference,
} from "@/lib/onboarding-preferences";
import { getPlatformProfileUrl } from "@/lib/platform-post-urls";

const connectionsSearchSchema = z.object({
	from: z.enum(["compose"]).optional().catch(undefined),
	type: z.enum(["text", "image", "video"]).optional().catch(undefined),
	onboarding: z.enum(["connect"]).optional().catch(undefined),
	oauth: z.enum(["success", "error"]).optional().catch(undefined),
	message: z.string().optional().catch(undefined),
	provider: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/dashboard/connect")({
	component: ConnectionsPage,
	validateSearch: (search) => connectionsSearchSchema.parse(search),
});

// --- Shared types & helpers ---

type ChannelSummary = {
	id: string;
	connected: boolean;
	displayName?: string | null;
	externalAccountId?: string;
	externalUsername?: string | null;
	provider: string;
};

async function apiRequest<T>(input: RequestInfo | URL, init?: RequestInit) {
	const response = await fetch(input, {
		...init,
		headers: {
			Accept: "application/json",
			...(init?.body ? { "Content-Type": "application/json" } : {}),
			...init?.headers,
		},
	});
	const payload = (await response.json().catch(() => null)) as
		| (T & { error?: string })
		| null;
	if (!response.ok) throw new Error(payload?.error || "Request failed");
	if (!payload) throw new Error("Request did not return JSON");
	return payload;
}

function useAllConnections(enabled: boolean, workspaceId?: string | null) {
	return useQuery({
		enabled: enabled && Boolean(workspaceId),
		queryFn: async () => {
			const res = await fetch("/api/social/accounts", {
				headers: { Accept: "application/json" },
			});
			if (!res.ok) return [];
			const data = (await res.json()) as { accounts: ChannelSummary[] };
			return data.accounts ?? [];
		},
		queryKey: ["settings", "all-connections", workspaceId],
	});
}

function looksLikeOpaqueHandle(value: string) {
	// Handles Pinterest-style auto-assigned usernames like
	// "0gxjhwq53vnmdnah2xgm7v1y0cz206" — long, all lowercase alphanumerics,
	// no separators. If the user has a real name saved as displayName, we'd
	// rather show that than an unreadable handle.
	return value.length >= 20 && /^[a-z0-9]+$/.test(value);
}

function getIdentity(channel: ChannelSummary) {
	const displayName = channel.displayName?.trim();
	const username = channel.externalUsername?.trim();
	if (
		displayName &&
		username &&
		displayName !== username &&
		looksLikeOpaqueHandle(username)
	) {
		return displayName;
	}
	if (username) return username.startsWith("@") ? username : `@${username}`;
	return displayName || channel.externalAccountId || null;
}

// --- Platform config ---

type Platform = {
	key: PlatformKey;
	name: string;
	icon: (className: string) => ReactNode;
	iconColor: string;
	iconBg: string;
	btnClass: string;
	oauthUrl: string;
	/** Extra connect options (e.g. Instagram has Facebook login too) */
	extraConnect?: {
		label: string;
		url: string;
		icon: (c: string) => ReactNode;
	}[];
	/** Uses app-password form instead of OAuth */
	appPasswordFlow?: boolean;
	/** Whether the platform is currently supported */
	enabled?: boolean;
};

const PLATFORMS: Platform[] = [
	{
		key: "instagram",
		name: "Instagram",
		icon: PLATFORM_ICONS.instagram,
		iconColor: "text-pink-500",
		iconBg: "bg-pink-500/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/instagram/oauth/start",
		enabled: true,
	},
	{
		key: "facebook",
		name: "Facebook",
		icon: PLATFORM_ICONS.facebook,
		iconColor: "text-[#1877F2]",
		iconBg: "bg-[#1877F2]/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/facebook/oauth/start",
		enabled: true,
	},
	{
		key: "linkedin",
		name: "LinkedIn",
		icon: PLATFORM_ICONS.linkedin,
		iconColor: "text-blue-600",
		iconBg: "bg-blue-600/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/linkedin/oauth/start",
		enabled: true,
	},
	{
		key: "x",
		name: "X",
		icon: PLATFORM_ICONS.x,
		iconColor: "text-foreground",
		iconBg: "bg-foreground/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/x/oauth/start",
		enabled: true,
	},
	{
		key: "tiktok",
		name: "TikTok",
		icon: PLATFORM_ICONS.tiktok,
		iconColor: "text-foreground",
		iconBg: "bg-foreground/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/tiktok/oauth/start",
		enabled: true,
	},
	{
		key: "youtube",
		name: "YouTube",
		icon: PLATFORM_ICONS.youtube,
		iconColor: "text-red-500",
		iconBg: "bg-red-500/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/youtube/oauth/start",
		enabled: true,
	},
	{
		key: "pinterest",
		name: "Pinterest",
		icon: PLATFORM_ICONS.pinterest,
		iconColor: "text-red-600",
		iconBg: "bg-red-600/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/pinterest/oauth/start",
		enabled: true,
	},
	{
		key: "threads",
		name: "Threads",
		icon: PLATFORM_ICONS.threads,
		iconColor: "text-foreground",
		iconBg: "bg-neutral-500/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "/api/social/threads/oauth/start",
		enabled: true,
	},
	{
		key: "bluesky",
		name: "Bluesky",
		icon: PLATFORM_ICONS.bluesky,
		iconColor: "text-blue-500",
		iconBg: "bg-blue-500/10",
		btnClass: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
		oauthUrl: "",
		appPasswordFlow: true,
		enabled: true,
	},
];

// --- Page ---

function ConnectionsPage() {
	const posthog = usePostHog();
	const { session, user } = useSession();
	const {
		from,
		type: composeType,
		onboarding,
		oauth,
		message,
		provider,
	} = Route.useSearch();
	const { workspace } = useDashboardContext();
	const navigate = useNavigate();
	const enabled = !!session && !!user;
	const shouldUseOnboardingReturn =
		onboarding === "connect" ||
		workspace?.onboardingStatus === "workspace_created";
	const onboardingContentFormats = parseOnboardingContentFormats(
		workspace?.onboardingContentFormats,
	);
	const onboardingPriorityPlatforms = parseOnboardingPriorityPlatforms(
		workspace?.onboardingPriorityPlatforms,
	);

	const accountsQuery = useAllConnections(enabled, workspace?.id);
	const accounts = accountsQuery.data ?? [];

	// Group connected channels by provider — multiple accounts per platform supported.
	const connectedByProvider = new Map<string, ChannelSummary[]>();
	for (const account of accounts) {
		if (!account.connected) continue;
		const list = connectedByProvider.get(account.provider) ?? [];
		list.push(account);
		connectedByProvider.set(account.provider, list);
	}

	// Toast on OAuth callback, then clear search params.
	// Guard against React StrictMode double-invoking the effect before the
	// navigate() call has cleared the search params.
	const handledOAuthKey = useRef<string | null>(null);
	useEffect(() => {
		if (!oauth || !provider) return;
		const key = `${oauth}:${provider}:${message ?? ""}`;
		if (handledOAuthKey.current === key) return;
		handledOAuthKey.current = key;
		const platformLabel =
			PLATFORMS.find((p) => p.key === provider)?.name ?? provider;
		if (oauth === "success") {
			posthog?.capture("social_account_connected", { provider });
			toast.success(`${platformLabel} connected successfully`);
		} else {
			toast.error(message || `Failed to connect ${platformLabel}`);
		}

		let returnTo: string | null = null;
		try {
			returnTo = localStorage.getItem("oauth_return_to");
			if (returnTo) localStorage.removeItem("oauth_return_to");
		} catch {
			// ignore storage errors
		}

		if (
			oauth === "success" &&
			returnTo &&
			returnTo.startsWith("/") &&
			returnTo !== "/dashboard/connect"
		) {
			navigate({ to: returnTo, replace: true });
			return;
		}

		navigate({ to: "/dashboard/connect", search: {}, replace: true });
	}, [oauth, message, navigate, posthog, provider]);

	const isLoading = accountsQuery.isLoading;
	const connected = PLATFORMS.flatMap((platform) => {
		const channels = connectedByProvider.get(platform.key) ?? [];
		return channels.map((channel) => ({ channel, platform }));
	});
	// Hide platforms that aren't live yet (`enabled` unset or false).
	const supportedPlatforms = sortPlatformsByOnboardingPreference(
		PLATFORMS.filter((p) => p.enabled),
		{
			contentFormats: onboardingContentFormats,
			priorityPlatforms: onboardingPriorityPlatforms,
		},
	);
	useEffect(() => {
		if (!workspace?.id) return;
		const shouldContinueToChat =
			workspace.onboardingStatus === "workspace_created";
		if (
			workspace.onboardingStatus === "account_connected" ||
			workspace.onboardingStatus === "first_draft_created" ||
			workspace.onboardingStatus === "first_post_scheduled" ||
			workspace.onboardingStatus === "completed"
		) {
			return;
		}
		if (connected.length === 0) return;

		void apiRequest("/api/workspaces", {
			method: "PATCH",
			body: JSON.stringify({
				workspaceId: workspace.id,
				onboardingStatus: "account_connected",
			}),
		})
			.then(() => {
				if (shouldContinueToChat) {
					navigate({ to: "/dashboard/chat", replace: true });
				}
			})
			.catch(() => {
				// Non-blocking: the connection itself succeeded, so avoid showing an
				// onboarding-only error in the main flow.
			});
	}, [connected.length, navigate, workspace?.id, workspace?.onboardingStatus]);

	return (
		<DashboardLayout>
			<DashboardHeaderSection>
				<DashboardPageHeader
					title="Connected accounts"
					description="Link one or more accounts per platform so AgentReacher can publish on your behalf."
				/>
			</DashboardHeaderSection>
			<DashboardContent>
				{from === "compose" ? (
					<div className="mb-4 flex flex-col items-start gap-3 rounded-md border border-border bg-muted/30 px-4 py-2.5 sm:flex-row sm:items-center sm:justify-between">
						<p className="text-sm text-muted-foreground">
							Connect an account to keep going with your post.
						</p>
						<Link
							className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
							to="/dashboard/compose"
							search={{ type: composeType }}
						>
							<ArrowLeft className="size-3.5" />
							Back to compose
						</Link>
					</div>
				) : null}
				{isLoading ? (
					<div className="flex items-center justify-center py-16">
						<Loader2 className="size-5 animate-spin text-muted-foreground" />
					</div>
				) : (
					<div className="divide-y divide-border rounded-md border border-border bg-card">
						{supportedPlatforms.map((platform) => (
							<PlatformRow
								key={platform.key}
								shouldUseOnboardingReturn={shouldUseOnboardingReturn}
								platform={platform}
								channels={connectedByProvider.get(platform.key) ?? []}
							/>
						))}
					</div>
				)}
			</DashboardContent>
		</DashboardLayout>
	);
}

// --- Row ---

function PlatformRow({
	platform,
	channels,
	shouldUseOnboardingReturn,
}: {
	platform: Platform;
	channels: ChannelSummary[];
	shouldUseOnboardingReturn: boolean;
}) {
	const [blueskyOpen, setBlueskyOpen] = useState(false);
	const rememberOnboardingReturn = () => {
		if (!shouldUseOnboardingReturn) return;
		try {
			localStorage.setItem("oauth_return_to", "/dashboard/chat");
		} catch {
			// ignore storage errors
		}
	};
	const connectButtonClass = `inline-flex h-9 flex-1 items-center justify-center rounded-md px-3 text-xs font-medium transition-colors sm:h-8 sm:flex-initial sm:min-w-[160px] ${platform.btnClass}`;
	const connectButton = platform.appPasswordFlow ? (
		<button
			type="button"
			onClick={() => setBlueskyOpen(true)}
			className={connectButtonClass}
		>
			Connect {platform.name}
		</button>
	) : (
		<a
			href={platform.oauthUrl}
			target="_blank"
			rel="noopener noreferrer"
			onClick={rememberOnboardingReturn}
			className={connectButtonClass}
		>
			Connect {platform.name}
		</a>
	);

	return (
		<div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
			<div className="flex items-center gap-3 sm:contents">
				<div
					className={`flex size-8 shrink-0 items-center justify-center rounded-md ${platform.iconBg}`}
				>
					{platform.icon(`size-4 ${platform.iconColor}`)}
				</div>
				<div className="flex flex-1 flex-wrap items-center gap-2 sm:contents">
					{connectButton}
					{platform.extraConnect?.map((extra) => (
						<a
							key={extra.url}
							href={extra.url}
							target="_blank"
							rel="noopener noreferrer"
							className="inline-flex h-9 flex-1 items-center justify-center rounded-md border border-border bg-background px-3 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground sm:h-8 sm:flex-initial"
						>
							{extra.label}
						</a>
					))}
				</div>
			</div>
			{channels.length > 0 && (
				<div className="flex min-w-0 flex-wrap items-center gap-2 sm:flex-1">
					{channels.map((channel) => (
						<AccountChip
							key={channel.id}
							platform={platform}
							channel={channel}
						/>
					))}
				</div>
			)}
			{platform.appPasswordFlow && (
				<BlueskyConnectDialog
					open={blueskyOpen}
					onOpenChange={setBlueskyOpen}
				/>
			)}
		</div>
	);
}

// --- Account chip ---

function AccountChip({
	platform,
	channel,
}: {
	platform: Platform;
	channel: ChannelSummary;
}) {
	const posthog = usePostHog();
	const queryClient = useQueryClient();
	const [isDisconnecting, setIsDisconnecting] = useState(false);
	const label = getIdentity(channel) ?? "Connected";
	const profileUrl = getPlatformProfileUrl(
		platform.key,
		channel.externalUsername,
		channel.externalAccountId,
	);

	const handleDisconnect = async () => {
		setIsDisconnecting(true);
		try {
			await apiRequest(
				`/api/social/${platform.key}/connection?channelId=${encodeURIComponent(channel.id)}`,
				{ method: "DELETE" },
			);
			await queryClient.invalidateQueries({
				queryKey: ["settings", "all-connections"],
			});
			posthog?.capture("social_account_disconnected", {
				provider: platform.key,
			});
			toast.success(`${platform.name} disconnected`);
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: `Failed to disconnect ${platform.name}`,
			);
			setIsDisconnecting(false);
		}
	};

	return (
		<span
			className={
				"inline-flex items-center gap-1.5 rounded-full border border-border bg-background py-1 pl-2 pr-1 text-xs text-foreground" +
				(isDisconnecting ? " opacity-60" : "")
			}
		>
			<span className="flex size-4 shrink-0 items-center justify-center">
				{platform.icon(`size-3 ${platform.iconColor}`)}
			</span>
			<span className="max-w-[160px] truncate">{label}</span>
			{profileUrl ? (
				<a
					href={profileUrl}
					target="_blank"
					rel="noopener noreferrer"
					aria-label={`Open ${label} profile`}
					className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
				>
					<ExternalLink className="size-3" />
				</a>
			) : null}
			<button
				type="button"
				onClick={handleDisconnect}
				disabled={isDisconnecting}
				aria-label={`Disconnect ${label}`}
				className="inline-flex size-5 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
			>
				{isDisconnecting ? (
					<Loader2 className="size-3 animate-spin" />
				) : (
					<X className="size-3" />
				)}
			</button>
		</span>
	);
}
